import { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getBottomSnackbarStackSlots } from './snackbarStyles';
import { useObservedElementSize } from './useObservedElementSize';

type BottomSnackbarStackHorizontal = 'center' | 'left' | 'right';

interface SnackbarStackItem {
  height: number;
  horizontal: BottomSnackbarStackHorizontal;
  minimumStackIndex: number;
  open: boolean;
  order: number;
  slots: number;
  width: number;
}

interface SnackbarStackItemUpdate {
  height: number;
  horizontal: BottomSnackbarStackHorizontal;
  minimumStackIndex: number;
  open: boolean;
  slots: number;
  width: number;
}

interface SnackbarStackContextValue {
  getStackIndex: (id: string) => number;
  getStackHeight: (horizontal: BottomSnackbarStackHorizontal) => number;
  getStackMaxWidth: (horizontal: BottomSnackbarStackHorizontal) => number;
  register: (id: string, item: SnackbarStackItemUpdate) => void;
  unregister: (id: string) => void;
}

const SnackbarStackContext = createContext<SnackbarStackContextValue | null>(null);

export const SnackbarStackProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<Record<string, SnackbarStackItem>>({});
  const nextOrderRef = useRef(0);

  const register = useCallback((id: string, item: SnackbarStackItemUpdate) => {
    setItems((currentItems) => {
      const currentItem = currentItems[id];
      const nextItem: SnackbarStackItem = {
        ...item,
        height: Math.max(0, item.height),
        minimumStackIndex: Math.max(0, item.minimumStackIndex),
        order: currentItem?.order ?? nextOrderRef.current++,
        slots: Math.max(0, item.slots),
        width: Math.max(0, item.width),
      };

      if (
        currentItem?.horizontal === nextItem.horizontal &&
        currentItem.height === nextItem.height &&
        currentItem.minimumStackIndex === nextItem.minimumStackIndex &&
        currentItem.open === nextItem.open &&
        currentItem.slots === nextItem.slots &&
        currentItem.width === nextItem.width
      ) {
        return currentItems;
      }

      return {
        ...currentItems,
        [id]: nextItem,
      };
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setItems((currentItems) => {
      if (!currentItems[id]) {
        return currentItems;
      }

      const remainingItems = { ...currentItems };
      delete remainingItems[id];
      return remainingItems;
    });
  }, []);

  const getStackLayout = useCallback(
    (horizontal: BottomSnackbarStackHorizontal) => {
      const indexes: Record<string, number> = {};
      let height = 0;

      Object.entries(items)
        .filter(([, item]) => item.horizontal === horizontal && item.open)
        .sort(([, left], [, right]) => left.order - right.order)
        .forEach(([id, item]) => {
          const stackIndex = Math.max(height, item.minimumStackIndex);
          indexes[id] = stackIndex;
          height = stackIndex + getBottomSnackbarStackSlots(item.height, item.slots);
        });

      return { height, indexes };
    },
    [items]
  );

  const getStackIndex = useCallback(
    (id: string) => {
      const item = items[id];
      if (!item) {
        return 0;
      }

      return getStackLayout(item.horizontal).indexes[id] ?? 0;
    },
    [getStackLayout, items]
  );

  const getStackHeight = useCallback((horizontal: BottomSnackbarStackHorizontal) => getStackLayout(horizontal).height, [getStackLayout]);
  const getStackMaxWidth = useCallback(
    (horizontal: BottomSnackbarStackHorizontal) =>
      Object.values(items)
        .filter((item) => item.horizontal === horizontal && item.open)
        .reduce((maxWidth, item) => Math.max(maxWidth, item.width), 0),
    [items]
  );

  const value = useMemo(
    () => ({
      getStackHeight,
      getStackIndex,
      getStackMaxWidth,
      register,
      unregister,
    }),
    [getStackHeight, getStackIndex, getStackMaxWidth, register, unregister]
  );

  return <SnackbarStackContext.Provider value={value}>{children}</SnackbarStackContext.Provider>;
};

export function useBottomSnackbarStackIndex({
  disabled = false,
  height = 0,
  horizontal,
  minimumStackIndex = 0,
  open,
  slots = 1,
  width = 0,
}: {
  disabled?: boolean;
  height?: number;
  horizontal: BottomSnackbarStackHorizontal;
  minimumStackIndex?: number;
  open: boolean;
  slots?: number;
  width?: number;
}): number {
  const stackContext = useContext(SnackbarStackContext);
  const id = useId();
  const register = stackContext?.register;
  const unregister = stackContext?.unregister;

  useLayoutEffect(() => {
    if (!register || !unregister || disabled) {
      unregister?.(id);
      return;
    }

    register(id, { height, horizontal, minimumStackIndex, open, slots, width });
  }, [disabled, height, horizontal, id, minimumStackIndex, open, register, slots, unregister, width]);

  useLayoutEffect(() => {
    return () => {
      unregister?.(id);
    };
  }, [id, unregister]);

  if (!stackContext || disabled) {
    return 0;
  }

  return stackContext.getStackIndex(id);
}

export function useMeasuredBottomSnackbarStackIndex<T extends HTMLElement>({
  disabled = false,
  horizontal,
  minimumStackIndex = 0,
  open,
  slots = 1,
}: {
  disabled?: boolean;
  horizontal: BottomSnackbarStackHorizontal;
  minimumStackIndex?: number;
  open: boolean;
  slots?: number;
}): {
  ref: (element: T | null) => void;
  stackIndex: number;
} {
  const { height, present, ref, width } = useObservedElementSize<T>(!disabled);
  const stackIndex = useBottomSnackbarStackIndex({
    disabled,
    height,
    horizontal,
    minimumStackIndex,
    open: open || present,
    slots,
    width,
  });

  return { ref, stackIndex };
}

export function useBottomSnackbarStackHeight(horizontal: BottomSnackbarStackHorizontal): number {
  const stackContext = useContext(SnackbarStackContext);

  return stackContext?.getStackHeight(horizontal) ?? 0;
}

export function useBottomSnackbarStackMaxWidth(horizontal: BottomSnackbarStackHorizontal): number {
  const stackContext = useContext(SnackbarStackContext);

  return stackContext?.getStackMaxWidth(horizontal) ?? 0;
}
